import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { claimCheck } from '@/services/email';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useRefundDetails } from '../../hooks/useRefundDetails';
import { useEffect } from 'react';
import Layout from '../Layout';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { ERefundType, EntityType } from '../../services/direct';
import { IconButton } from '../ui/IconButton';
import { Check } from 'lucide-react';
import { StateComboBox } from '../ui/StateComboBox';
import { CountryComboBox } from '../ui/CountryComboBox';
import { useTranslation } from 'react-i18next';

const CheckForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const formSchema = z.object({
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    business_payee_name: z.string().optional(),
    refund_to_address_line1: z.string().optional(),
    refund_to_address_line2: z.string().optional(),
    refund_to_city: z.string().optional(),
    refund_to_state: z.string().optional(),
    refund_to_zip: z.string().optional(),
    refund_to_country: z.string().optional(),
  });

  const { data } = useRefundDetails();

  const showPayeeName = data?.refundData?.isCustomPaymentTypesEnabled
    ? data?.refundData?.entity_type === EntityType.BUSINESS ||
      data?.refundData?.entity_type === EntityType.UNKNOWN
    : data?.refundData?.refund_type === ERefundType.BUSINESS;

  const claimCheckMutation = useMutation({
    mutationFn: claimCheck,
    onSuccess: () => {
      navigate({
        to: `/payments/thank-you`,
      });
    },
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    disabled: data?.refundData?.allow_address_update === false,
    defaultValues: {
      first_name: '',
      last_name: '',
      business_payee_name: '',
      refund_to_address_line1: '',
      refund_to_address_line2: '',
      refund_to_city: '',
      refund_to_state: '',
      refund_to_zip: '',
      refund_to_country: '',
    },
  });

  const {
    formState: { isDirty },
  } = form;
  useEffect(() => {
    if (!data?.refundData) return;

    form.reset({
      first_name: data.refundData.first_name || '',
      last_name: data.refundData.last_name || '',
      business_payee_name: data.refundData.business_payee_name || '',
      refund_to_address_line1: data.refundData.refund_to_address_line1 || '',
      refund_to_address_line2: data.refundData.refund_to_address_line2 || '',
      refund_to_city: data.refundData.refund_to_city || '',
      refund_to_state: data.refundData.refund_to_state || '',
      refund_to_zip: data.refundData.refund_to_zip || '',
      refund_to_country: data.refundData.refund_to_country || '',
    });
  }, [data?.refundData]);

  const onSubmit = async () => {
    try {
      claimCheckMutation.mutate({
        isUpdated: isDirty,
        address: {
          refund_to_address_line1:
            form.getValues('refund_to_address_line1') ?? '',
          refund_to_address_line2:
            form.getValues('refund_to_address_line2') ?? '',
          refund_to_city: form.getValues('refund_to_city') ?? '',
          refund_to_state: form.getValues('refund_to_state') ?? '',
          refund_to_zip: form.getValues('refund_to_zip') ?? '',
          refund_to_country: form.getValues('refund_to_country') ?? '',
        },
        isAddressUpdateAllowed: data?.refundData?.allow_address_update ?? false,
      });
    } catch (e) {
      throw new Error(t('Failed to claim Payment via email'));
    }
  };

  return (
    <Layout className='p-0'>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className='rounded-md border border-border bg-card p-6 pt-6'>
            <h6 className='mb-6 font-normal text-primary-600'>
              {t('Personal Details')}
            </h6>
            <div className='flex flex-col gap-3 lg:grid lg:grid-cols-2 lg:gap-6'>
              {showPayeeName ? (
                <FormField
                  control={form.control}
                  name='business_payee_name'
                  render={({ field }) => (
                    <FormItem className='w-full'>
                      <FormLabel>{t('Payee Name')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='Business Name'
                          {...field}
                          disabled
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <>
                  <FormField
                    control={form.control}
                    name='first_name'
                    render={({ field }) => (
                      <FormItem className='w-full'>
                        <FormLabel>{t('First Name')}</FormLabel>
                        <FormControl>
                          <Input placeholder='John' {...field} disabled />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name='last_name'
                    render={({ field }) => (
                      <FormItem className='w-full'>
                        <FormLabel>{t('Last Name')}</FormLabel>
                        <FormControl>
                          <Input placeholder='Doe' {...field} disabled />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
            </div>
            <hr className='my-5 border-dashed lg:my-10' />
            <h6 className='mb-6 font-normal text-primary-600'>
              {t('Address details')}
            </h6>
            <div className='flex flex-col gap-3 lg:grid lg:grid-cols-2 lg:gap-6'>
              <FormField
                control={form.control}
                name='refund_to_address_line1'
                render={({ field }) => (
                  <FormItem className='w-full'>
                    <FormLabel>{t('Address')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder='Street address, P.O. box'
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      {t('Type street address line')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='refund_to_address_line2'
                render={({ field }) => (
                  <FormItem className='w-full'>
                    <FormLabel>{t('Address 2 (optional)')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder='Apartment, suite, unit, building, floor'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='refund_to_city'
                render={({ field }) => (
                  <FormItem className='w-full'>
                    <FormLabel className='font-medium'>{t('City')}</FormLabel>
                    <FormControl>
                      <Input placeholder='Name of the city' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <StateComboBox
                disabled={data?.refundData?.allow_address_update === false}
                control={form.control}
                country={form.watch('refund_to_country')}
              />
              <FormField
                control={form.control}
                name='refund_to_zip'
                render={({ field }) => (
                  <FormItem className='w-full'>
                    <FormLabel>
                      {form.watch('refund_to_country') === 'CA'
                        ? t('Postal code')
                        : t('Zip code')}
                    </FormLabel>
                    <FormControl>
                      <Input placeholder='000 000' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <CountryComboBox
                disabled={
                  data?.refundData?.allow_address_update === false ||
                  Boolean(data?.refundData?.refund_to_country)
                }
                control={form.control}
              />
            </div>
            <div className='mt-6 flex justify-end gap-2.5'>
              <IconButton
                Icon={Check}
                isLoading={claimCheckMutation.isPending}
                type='submit'
                variant='success'
              >
                {t('Claim Payment')}
              </IconButton>
            </div>
          </div>
        </form>
      </Form>
    </Layout>
  );
};

export default CheckForm;
